/**
 * ExtensionBridge - Secure communication between web app and browser extension
 * 
 * This service provides a secure channel for the browser extension to access
 * user data ONLY when:
 * 1. The user is authenticated on the web app
 * 2. The request comes from our official extension
 * 3. The request includes proper authentication tokens
 * 
 * The extension NEVER gets direct access to decrypted data storage.
 * All data access goes through this bridge.
 */

import secureStorage from './secureStorage';
import axios from 'axios';
import { API_BASE } from './api';

const EXTENSION_BRIDGE_VERSION = '1.0.0';

// browser.connectExtension (Ajustes > Navegador). El estado vive en la cuenta; este flag es la copia
// local para dos lectores que no pueden preguntar al servidor: la extension (su content script lo lee
// del localStorage del panel, ver browser-extension/content.js) y este mismo puente al arrancar,
// antes de que lleguen los ajustes. Sin clave = encendida (valor por defecto del ajuste).
const CONNECTION_FLAG = 'sgcExtensionConnection';

// Safety limits to avoid OOM when vaults reach tens of thousands of items.
const MAX_MATCHING_CREDENTIALS = 50;
const MAX_SEARCH_RESULTS = 200;
const MAX_ALL_CREDENTIALS = 5000;

class ExtensionBridge {
  constructor() {
    this.isInitialized = false;
    this.sessionToken = null;
    this.rfidId = null;
    this.messageHandlers = new Map();
    this.pendingRequests = new Map();
    this.extensionId = null;
    // true | false | null (aun no se sabe: sin flag local y sin respuesta de ajustes). Mientras no sea
    // true no se empuja la sesion ni se responde a RFID_VAULT_* salvo al ping.
    this.connectionEnabled = null;
    this._extReqCounter = 0;
    
    // Bind message handler
    this.handleMessage = this.handleMessage.bind(this);
    this.handleStorage = this.handleStorage.bind(this);
  }

  readConnectionFlag() {
    try {
      const v = localStorage.getItem(CONNECTION_FLAG);
      return v === null ? null : v !== 'off';
    } catch {
      return null;
    }
  }

  /**
   * Fija la conexion con la extension. Encendida: se empuja la sesion (como al iniciar). Apagada:
   * no se envia nada mas y se avisa a la extension, que conserva su propia sesion (decision y motivo en
   * browser-extension/background.js). No emite RFID_VAULT_SESSION_CLEARED: apagar no es cerrar sesion.
   * @param {boolean} enabled
   * @param {{announce?: boolean}} options announce=false cuando el cambio ya lo anuncio otra pestaña.
   */
  setConnectionEnabled(enabled, { announce = true } = {}) {
    const next = enabled !== false;
    const changed = this.connectionEnabled !== next;
    this.connectionEnabled = next;
    try { localStorage.setItem(CONNECTION_FLAG, next ? 'on' : 'off'); } catch { /* sin almacenamiento */ }
    if (!changed && announce === false) return;
    if (announce) {
      window.postMessage({ type: 'RFID_VAULT_CONNECTION_STATE', enabled: next, timestamp: Date.now() }, '*');
    }
    if (next && this.isInitialized) {
      this._readyBroadcasted = false;
      this._lastPushedSig = null;
      this.broadcastBridgeReady();
    }
  }

  /** Otra pestaña del panel cambio el ajuste: este puente lo sigue sin volver a anunciarlo. */
  handleStorage(event) {
    if (event.key !== CONNECTION_FLAG) return;
    this.setConnectionEnabled(event.newValue !== 'off', { announce: false });
  }

  /** Si el ajuste de la cuenta (ya leido por la pagina de Ajustes) difiere de lo que sabe el puente, manda el ajuste. */
  reconcileConnection(enabled) {
    if (this.connectionEnabled !== (enabled !== false)) this.setConnectionEnabled(enabled);
  }

  async resolveConnection() {
    const local = this.readConnectionFlag();
    if (local !== null) this.connectionEnabled = local;
    try {
      const response = await axios.get(`${API_BASE}/api/account/settings`, {
        headers: { Authorization: `Bearer ${this.sessionToken}` },
        timeout: 10000
      });
      if (!this.isInitialized) return; // se cerro la sesion mientras tanto
      const value = response?.data?.browser?.connectExtension !== false;
      this.setConnectionEnabled(value, { announce: local !== value });
    } catch (error) {
      // Sin ajustes: manda lo ultimo que se supo en este navegador; sin nada, encendida (el valor por defecto).
      if (this.isInitialized && this.connectionEnabled === null) this.setConnectionEnabled(true, { announce: false });
    }
  }

  /**
   * Initialize the bridge with session information
   * @param {string} sessionToken - User's auth token
   * @param {string} rfidId - User's RFID ID
   */
  initialize(sessionToken, rfidId) {
    this.sessionToken = sessionToken;
    this.rfidId = rfidId;
    this.isInitialized = true;

    // Listen for messages from extension
    window.addEventListener('message', this.handleMessage);
    window.addEventListener('storage', this.handleStorage);

    // Expose a minimal API on window for extension discovery
    window.__RFID_VAULT_BRIDGE__ = {
      version: EXTENSION_BRIDGE_VERSION,
      isReady: () => this.isInitialized && this.connectionEnabled === true && secureStorage.isReady(),
      getSessionStatus: () => ({
        authenticated: this.isInitialized,
        hasSecureStorage: secureStorage.isReady()
      })
    };

    console.log('ExtensionBridge initialized');

    // La sesion solo se anuncia y se empuja si la conexion esta encendida. Con el flag local
    // conocido se actua al momento; sin el, se espera a los ajustes de la cuenta para no empujar
    // la sesion de alguien que la tiene apagada desde otro dispositivo.
    this.connectionEnabled = this.readConnectionFlag();
    if (this.connectionEnabled === true) this.broadcastBridgeReady();
    this.resolveConnection();
  }

  /**
   * Broadcast that the bridge is ready for connections.
   * One-shot: extra broadcasts trigger extension echo loops (OOM).
   * If the extension misses this single message, it can request session via
   * RFID_VAULT_GET_SESSION_INFO when it loads.
   */
  broadcastBridgeReady() {
    if (!this.isInitialized || this._readyBroadcasted || this.connectionEnabled !== true) return;
    this._readyBroadcasted = true;
    window.postMessage({
      type: 'RFID_VAULT_BRIDGE_READY',
      version: EXTENSION_BRIDGE_VERSION,
      timestamp: Date.now()
    }, '*');
    this.pushSessionToExtension();
  }

  /**
   * Push session to extension (call after login). One-shot per session/login.
   */
  pushSessionToExtension() {
    if (this.connectionEnabled !== true) return;
    if (!this.sessionToken || !this.rfidId) {
      console.log('ExtensionBridge: No session to push');
      return;
    }
    const sig = `${this.sessionToken}:${this.rfidId}`;
    if (this._lastPushedSig === sig) return; // already pushed this session
    this._lastPushedSig = sig;

    window.postMessage({
      type: 'RFID_VAULT_SESSION_AVAILABLE',
      token: this.sessionToken,
      rfidId: this.rfidId,
      timestamp: Date.now(),
      source: 'rfid-vault-webapp'
    }, '*');

    console.log('ExtensionBridge: Pushed session to extension');
  }

  /**
   * Clear session in extension (call on logout)
   */
  clearExtensionSession() {
    // Conexion apagada: el cierre de sesion del panel no toca la sesion de la extension.
    if (this.connectionEnabled !== true) return;
    window.postMessage({
      type: 'RFID_VAULT_SESSION_CLEARED',
      timestamp: Date.now()
    }, '*');

    console.log('ExtensionBridge: Notified extension to clear session');
  }

  /**
   * Ask the installed extension for its version. Used by the web UI to offer
   * an update. Resolves null if nothing answers within the timeout — either
   * the extension isn't installed, or it predates this message type.
   * @param {number} timeoutMs
   * @returns {Promise<string|null>}
   */
  getInstalledExtensionVersion(timeoutMs = 1500) {
    return new Promise((resolve) => {
      const requestId = this.generateResponseToken();
      const timer = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        resolve(null);
      }, timeoutMs);

      this.pendingRequests.set(requestId, (version) => {
        clearTimeout(timer);
        this.pendingRequests.delete(requestId);
        resolve(version || null);
      });

      window.postMessage({
        type: 'RFID_VAULT_GET_VERSION',
        requestId,
        timestamp: Date.now()
      }, window.location.origin);
    });
  }

  /**
   * Pregunta a la extension por un mensaje SGC_EXT_* (los contesta el content script de la extension en
   * las paginas del panel, aunque la conexion este apagada: no mueven sesion ni credenciales).
   * Resuelve null si nada contesta a tiempo (sin extension, o una version que no conoce el mensaje).
   */
  askExtension(type, payload = {}, timeoutMs = 3000) { // el service worker de la extension puede estar dormido: despertarlo cuesta algo mas de un segundo
    return new Promise((resolve) => {
      const requestId = `panel_${++this._extReqCounter}_${this.generateResponseToken().slice(0, 12)}`;
      const onMessage = (event) => {
        if (event.source !== window || event.origin !== window.location.origin) return;
        const d = event.data;
        if (!d || d.type !== 'SGC_EXT_REPLY' || d.requestId !== requestId) return;
        cleanup();
        resolve(d.data ?? null);
      };
      const timer = setTimeout(() => { cleanup(); resolve(null); }, timeoutMs);
      const cleanup = () => { clearTimeout(timer); window.removeEventListener('message', onMessage); };
      window.addEventListener('message', onMessage);
      window.postMessage({ type, requestId, ...payload }, window.location.origin);
    });
  }

  /**
   * Estado de la extension instalada: { version, lastSyncAt, hasSession, connectionOff } o null si no hay
   * extension. Las versiones anteriores a la fase 3 solo contestan a la version.
   */
  async getExtensionStatus() {
    // En paralelo: sin extension la espera es una sola, no la suma de las dos.
    const [status, version] = await Promise.all([this.askExtension('SGC_EXT_GET_STATUS'), this.getInstalledExtensionVersion(3000)]);
    if (status && status.version) return status;
    return version ? { version, lastSyncAt: null, hasSession: null, connectionOff: null } : null;
  }

  /** Sitios con "Nunca para este sitio" en la extension; null si no hay extension que lo gestione. */
  async getNeverSaveSites() {
    const res = await this.askExtension('SGC_EXT_NEVER_LIST');
    return Array.isArray(res?.sites) ? res.sites : null;
  }

  async removeNeverSaveSite(host) {
    const res = await this.askExtension('SGC_EXT_NEVER_REMOVE', { host });
    return Array.isArray(res?.sites) ? res.sites : null;
  }

  async clearNeverSaveSites() {
    const res = await this.askExtension('SGC_EXT_NEVER_CLEAR');
    return Array.isArray(res?.sites) ? res.sites : null;
  }

  /**
   * Validate that the message source is trusted
   * @param {MessageEvent} event - The message event
   * @returns {boolean}
   */
  isValidSource(event) {
    // Same origin is always valid
    if (event.origin === window.location.origin) {
      return true;
    }
    
    // Check for extension origins
    if (event.origin.startsWith('chrome-extension://') || 
        event.origin.startsWith('moz-extension://')) {
      return true;
    }
    
    return false;
  }

  /**
   * Generate a one-time response token for security
   */
  generateResponseToken() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Handle incoming messages from extension
   * @param {MessageEvent} event
   */
  async handleMessage(event) {
    // Validate source
    if (!this.isValidSource(event)) {
      return;
    }

    const { type, requestId, payload } = event.data || {};

    // Only handle RFID Vault messages
    if (!type || !type.startsWith('RFID_VAULT_')) {
      return;
    }

    // Drop messages WE originated. The extension's content script re-emits
    // these into the page; if we sendResponse() back, the extension treats
    // it as a new event and posts another message → infinite ping-pong → OOM.
    // These are pure notifications (no requestId), not requests, so silently ignore.
    const SELF_ORIGINATED = new Set([
      'RFID_VAULT_BRIDGE_READY',
      'RFID_VAULT_SESSION_AVAILABLE',
      'RFID_VAULT_SESSION_CLEARED',
      'RFID_VAULT_RESPONSE',
      'RFID_VAULT_GET_VERSION',
      'RFID_VAULT_CONNECTION_STATE'
    ]);
    if (SELF_ORIGINATED.has(type)) return;

    // Requests must carry a requestId so the response correlates to a caller.
    // If a message with one of our request types arrives without requestId,
    // treat it as a stray echo and drop it (also avoids the loop).
    if (!requestId) return;

    // Reply from the extension to a RFID_VAULT_GET_VERSION we sent. Not a
    // request we need to answer — resolve the matching pending promise and
    // stop, so it never falls into the "unknown message type" branch below.
    if (type === 'RFID_VAULT_VERSION') {
      const resolver = this.pendingRequests.get(requestId);
      if (resolver) resolver(event.data.version);
      return;
    }

    // Conexion apagada (o aun sin saber): solo se contesta al ping, para que la extension sepa que esta
    // desactivada. Nada de sesion, credenciales ni busquedas.
    if (this.connectionEnabled !== true) {
      if (type === 'RFID_VAULT_PING') {
        this.sendResponse(event.source, requestId, {
          status: 'ok',
          version: EXTENSION_BRIDGE_VERSION,
          connection: 'disabled'
        });
      }
      return;
    }

    // Check if bridge is ready
    if (!this.isInitialized || !secureStorage.isReady()) {
      this.sendResponse(event.source, requestId, {
        error: 'Bridge not initialized or user not authenticated',
        code: 'NOT_AUTHENTICATED'
      });
      return;
    }

    try {
      let response;

      switch (type) {
        case 'RFID_VAULT_PING':
          response = { 
            status: 'ok', 
            version: EXTENSION_BRIDGE_VERSION,
            authenticated: true 
          };
          break;

        case 'RFID_VAULT_GET_SESSION_INFO':
          response = await this.handleGetSessionInfo();
          break;

        case 'RFID_VAULT_GET_CREDENTIALS':
          response = await this.handleGetCredentials(payload);
          break;

        case 'RFID_VAULT_GET_ALL_CREDENTIALS':
          response = await this.handleGetAllCredentials();
          break;

        case 'RFID_VAULT_SEARCH_CREDENTIALS':
          response = await this.handleSearchCredentials(payload);
          break;

        case 'RFID_VAULT_GET_CREDENTIAL_PASSWORD':
          response = await this.handleGetCredentialPassword(payload);
          break;

        case 'RFID_VAULT_SAVE_CREDENTIAL':
          response = await this.handleSaveCredential(payload);
          break;

        default:
          response = { error: 'Unknown message type', code: 'UNKNOWN_TYPE' };
      }

      this.sendResponse(event.source, requestId, response);
    } catch (error) {
      console.error('ExtensionBridge error:', error);
      this.sendResponse(event.source, requestId, {
        error: error.message,
        code: 'INTERNAL_ERROR'
      });
    }
  }

  /**
   * Send response back to extension
   * @param {Window} source - The source window
   * @param {string} requestId - The request ID
   * @param {any} data - Response data
   */
  sendResponse(source, requestId, data) {
    if (!source) return;
    
    source.postMessage({
      type: 'RFID_VAULT_RESPONSE',
      requestId,
      data,
      timestamp: Date.now()
    }, window.location.origin);
  }

  /**
   * Handle get session info request
   */
  async handleGetSessionInfo() {
    return {
      authenticated: true,
      rfidId: this.rfidId,
      hasData: secureStorage.isReady()
    };
  }

  /**
   * Handle get credentials for a specific URL
   * @param {object} payload - { url: string }
   */
  async handleGetCredentials(payload) {
    if (!payload?.url) {
      return { error: 'URL is required', code: 'MISSING_URL' };
    }

    try {
      const domain = this.extractDomain(payload.url);

      const response = await axios.get(
        `${API_BASE}/api/data/${this.rfidId}?search=${encodeURIComponent(domain)}&limit=50&light=1`,
        { headers: { Authorization: `Bearer ${this.sessionToken}` }, timeout: 20000 }
      );

      const items = response?.data?.data || [];
      const matching = items.filter(cred => {
        if (this.isHiddenSystemItem(cred)) return false;
        if (!cred?.url) return false;
        const credDomain = this.extractDomain(cred.url);
        return this.domainsMatch(domain, credDomain);
      }).slice(0, MAX_MATCHING_CREDENTIALS);

      return {
        credentials: matching.map(cred => ({
          id: cred.id,
          title: cred.title || cred.name,
          username: cred.username || cred.email,
          url: cred.url,
          hasPassword: !!cred.password || cred.type === 'login',
          favorite: !!cred.favorite,
          type: cred.type || 'login',
          subtitle: cred.subtitle || ''
        }))
      };
    } catch (error) {
      return { error: error.message, code: 'FETCH_ERROR' };
    }
  }

  /**
   * Handle get all credentials (list without passwords)
   */
  async handleGetAllCredentials() {
    try {
      const response = await axios.get(
        `${API_BASE}/api/data/${this.rfidId}?limit=100&light=1`,
        { headers: { Authorization: `Bearer ${this.sessionToken}` }, timeout: 20000 }
      );

      const credentials = response?.data?.data || [];

      return {
        truncated: (response?.data?.totalCount || 0) > credentials.length,
        credentials: credentials.filter(cred => !this.isHiddenSystemItem(cred)).map(cred => ({
          id: cred.id,
          title: cred.title || cred.name,
          username: cred.username || cred.email,
          url: cred.url,
          hasPassword: !!cred.password || cred.type === 'login',
          favorite: !!cred.favorite,
          type: cred.type || 'login',
          subtitle: cred.subtitle || ''
        }))
      };
    } catch (error) {
      return { error: error.message, code: 'FETCH_ERROR' };
    }
  }

  /**
   * Handle search credentials
   * @param {object} payload - { query: string }
   */
  async handleSearchCredentials(payload) {
    if (!payload?.query) {
      return { error: 'Query is required', code: 'MISSING_QUERY' };
    }

    try {
      const response = await axios.get(
        `${API_BASE}/api/data/${this.rfidId}?search=${encodeURIComponent(payload.query)}&limit=100&light=1`,
        { headers: { Authorization: `Bearer ${this.sessionToken}` }, timeout: 20000 }
      );

      const matching = (response?.data?.data || []).filter(cred => !this.isHiddenSystemItem(cred));

      return {
        truncated: matching.length >= MAX_SEARCH_RESULTS,
        credentials: matching.map(cred => ({
          id: cred.id,
          title: cred.title || cred.name,
          username: cred.username || cred.email,
          url: cred.url,
          hasPassword: !!cred.password || cred.type === 'login',
          favorite: !!cred.favorite,
          type: cred.type || 'login',
          subtitle: cred.subtitle || ''
        }))
      };
    } catch (error) {
      return { error: error.message, code: 'SEARCH_ERROR' };
    }
  }

  /**
   * Handle get credential password (returns password for specific credential)
   * This is the only method that returns passwords, and only for one credential at a time
   * @param {object} payload - { id: string }
   */
  async handleGetCredentialPassword(payload) {
    if (!payload?.id) {
      return { error: 'Credential ID is required', code: 'MISSING_ID' };
    }

    try {
      if (!this.sessionToken || !this.rfidId) {
        return { error: 'Not authenticated', code: 'NOT_AUTHENTICATED' };
      }

      const response = await axios.get(
        `${API_BASE}/api/data/${this.rfidId}/item/${payload.id}`,
        { headers: { Authorization: `Bearer ${this.sessionToken}` }, timeout: 20000 }
      );

      const data = response?.data?.data;
      const password = data?.password ?? data?.pass ?? data?.secret ?? null;
      const username = data?.username ?? data?.email ?? null;

      if (!password) {
        return { error: 'Password not available for this item', code: 'NO_PASSWORD' };
      }

      return { id: payload.id, username, password };
    } catch (error) {
      return { error: error.message, code: 'FETCH_ERROR' };
    }
  }

  /**
   * Handle save credential request from extension
   * @param {object} payload - Credential data
   */
  async handleSaveCredential(payload) {
    if (!payload?.url || !payload?.username) {
      return { error: 'URL and username are required', code: 'MISSING_DATA' };
    }

    try {
      if (!this.sessionToken || !this.rfidId) {
        return { error: 'Not authenticated', code: 'NOT_AUTHENTICATED' };
      }

      const domain = this.extractDomain(payload.url);
      const itemData = {
        type: 'login',
        username: payload.username,
        email: payload.username,
        password: payload.password,
        url: payload.url,
        savedFromExtension: true
      };

      const response = await axios.post(
        `${API_BASE}/api/data/save`,
        {
          rfidId: this.rfidId,
          name: payload.title || domain,
          description: 'Saved from browser extension',
          data: itemData,
          tags: []
        },
        { headers: { Authorization: `Bearer ${this.sessionToken}` }, timeout: 20000 }
      );

      const savedId = response?.data?.id || response?.data?.data?.id || response?.data?._id;
      return {
        success: true,
        id: savedId,
        isUpdate: false
      };
    } catch (error) {
      return { error: error.message, code: 'SAVE_ERROR' };
    }
  }

  /**
   * Extract domain from URL
   * @param {string} url
   * @returns {string}
   */
  extractDomain(url) {
    try {
      const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
      return urlObj.hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  }

  /**
   * Check if two domains match (including subdomain matching)
   * @param {string} domain1
   * @param {string} domain2
   * @returns {boolean}
   */
  domainsMatch(domain1, domain2) {
    if (!domain1 || !domain2) return false;
    if (domain1 === domain2) return true;

    // Get main domain (last 2 parts)
    const getMainDomain = (domain) => {
      const parts = domain.split('.');
      return parts.length >= 2 ? parts.slice(-2).join('.') : domain;
    };

    return getMainDomain(domain1) === getMainDomain(domain2);
  }

  isHiddenSystemItem(cred) {
    const name = cred?.name || cred?.title || '';
    return cred?.type === 'otp_vault' || name === '__otp_entries__';
  }

  /**
   * Destroy the bridge (on logout)
   */
  destroy() {
    // Notify extension to clear its session
    this.clearExtensionSession();

    window.removeEventListener('message', this.handleMessage);
    window.removeEventListener('storage', this.handleStorage);

    if (window.__RFID_VAULT_BRIDGE__) {
      delete window.__RFID_VAULT_BRIDGE__;
    }

    this.isInitialized = false;
    this.connectionEnabled = null;
    this.sessionToken = null;
    this.rfidId = null;
    this._readyBroadcasted = false;
    this._lastPushedSig = null;

    // Broadcast that bridge is closing
    window.postMessage({
      type: 'RFID_VAULT_BRIDGE_CLOSED',
      timestamp: Date.now()
    }, '*');

    console.log('ExtensionBridge destroyed');
  }
}

// Singleton instance
const extensionBridge = new ExtensionBridge();
export default extensionBridge;
